import { Instance, SnapshotIn, getSnapshot, types, flow, cast, destroy, isStateTreeNode } from 'mobx-state-tree';
import { DataState, DataStateStore } from '@ace/core';

import { ExecutionsStore, IExecutionsStore } from './Executions.store';
import { AxisZone } from '@shared/components/ZoneAxis';
import { getExecutionLatestActivityMs, getStepCompletionStatus, getStepDesignedNumber, getStepErrorCount, getStepEvents, isErrorEventType, isMandatoryStep, isShortStep, StepCompletionStatus } from 'NeoAnalytics/executionSteps';

export type exeStep = {
    actorId: string,
    description: string | null,
    id: string,
    name: string,
    parentStepId: string | null,
    startTime: string,
    endTime: string | null,
    duration: string | null,
    details: unknown,

}

export function getExecutionStepSelectionKey(
    step: {
        id?: string | null;
        startTime?: string | null;
        endTime?: string | null;
        name?: string | null;
        parentStepId?: string | null;
    },
    index: number,
): string {
    return JSON.stringify({
        id: step.id ?? null,
        startTime: step.startTime ?? null,
        endTime: step.endTime ?? null,
        name: step.name ?? null,
        parentStepId: step.parentStepId ?? null,
        index,
    });
}
export type exeEvent = {
    actorId: string,
    description: string,
    name: string,
    value: unknown,
    type: string,
    timeStamp: string,
}
export type Execution = {
    actorIds: string[],
    additionalData: unknown,
    companyId: string,
    createdAt: string,
    createdBy: string,
    duration: string,
    endReason: string,
    endTime: string,
    entityAdditionalData: string,
    entityDescription: string,
    entityId: string,
    entityName: string,
    entityVersion: string,
    executionMode: string,
    id: string,
    isCompleted: boolean,
    platform: string,
    startTime: string,
    totalErrors: number,
    totalSteps: number,
    updatedAt: string,
    updatedBy: string,
    steps: exeStep[],
    events: exeEvent[]
}

export type FormattedStep = {
    name: string;
    interval: number[];
    description: string | null;
    stepId: string;
    hasErrors: boolean;
    errorCount: number;
    nonErrorEventCount: number;
    completionStatus: StepCompletionStatus;
    isMandatory: boolean;
    designedStepNumber: string | null;
    isShort: boolean;
    isStartedOnly: boolean;
    parentStepId: string | null;
    hasChildren: boolean;
}

export type AdditionalItem = { key: string; value: string };

const KNOWN_PREFIXES = ['Info:', 'Summary:'] as const;
type KnownPrefix = typeof KNOWN_PREFIXES[number];

function rawToString(raw: unknown, emptyValue: string): string {
    if (raw == null) {return emptyValue;}
    if (typeof raw === 'string') {return raw;}
    if (typeof raw === 'number' || typeof raw === 'boolean') {return String(raw);}
    try { return JSON.stringify(raw); } catch { return emptyValue; }
}

export function additionalDataToItems( value: unknown, options?: { sortKeys?: boolean; emptyValue?: string }): AdditionalItem[] {
    const emptyValue = options?.emptyValue ?? "—";

    if (!value || typeof value !== "object" || Array.isArray(value)) {return [];}

    const entries = Object.entries(value as Record<string, unknown>);
    if (options?.sortKeys) {entries.sort(([a], [b]) => a.localeCompare(b));}

    return entries.map(([key, raw]) => ({
        key,
        value: rawToString(raw, emptyValue),
    }));
}

export function partitionAdditionalData(
    value: unknown,
    options?: { sortKeys?: boolean; emptyValue?: string },
): { infoItems: AdditionalItem[]; summaryItems: AdditionalItem[] } {
    const emptyValue = options?.emptyValue ?? '—';

    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return { infoItems: [], summaryItems: [] };
    }

    const entries = Object.entries(value as Record<string, unknown>);
    if (options?.sortKeys) {entries.sort(([a], [b]) => a.localeCompare(b));}

    const infoItems: AdditionalItem[] = [];
    const summaryItems: AdditionalItem[] = [];

    for (const [key, raw] of entries) {
        const matched = KNOWN_PREFIXES.find(p => key.startsWith(p));
        const strippedKey = matched ? key.slice(matched.length).trimStart() : key;
        const item: AdditionalItem = { key: strippedKey, value: rawToString(raw, emptyValue) };

        if (!matched || (matched as KnownPrefix) === 'Info:') {
            infoItems.push(item);
        } else if ((matched as KnownPrefix) === 'Summary:') {
            summaryItems.push(item);
        }
    }

    return { infoItems, summaryItems };
}

export const ExecutionsUIStoreInferred = types
    .model('ExecutionsUIStoreInferred', {
        _execution: types.optional(ExecutionsStore, {}),
        executionsUserId: types.maybeNull(types.string),
        executionsUserName: types.maybeNull(types.string),
        executionsUserRole: types.maybeNull(types.string),
        executionData: types.maybeNull(ExecutionsStore),
        selectedExecutionId: types.maybeNull(types.string),
        selectedStepId: types.maybeNull(types.string),
        stepModalOpen: types.optional(types.boolean, false),
        errors: types.maybeNull(types.array(types.string)),
    })
    .volatile(() => ({
        status: DataStateStore.create({ state: DataState.initial }),
    }))
    .views(self => ({
        get execution(){
            return self._execution;
        },
        get formattedExecution() {

            if (!self.selectedExecutionId && self._execution) {return null;}
            const execution = self._execution;

            const formattedSteps: FormattedStep[] = [];
            const formattedAxisSteps: AxisZone[] = [];
            const formattedEvents: Record<string, any>[] = [];
            const eventTypes: string[] = [];

            let exeDuration: number=0;

            const seenStepKeys = new Set<string>();

            if(execution){
                const exeStart = Date.parse(execution.startTime!);
                const executionActivityEndMs = Math.max(getExecutionLatestActivityMs(execution), exeStart + 500);

                if(execution.steps && execution.steps.length > 0){
                    let lastStep = execution.steps[execution.steps.length - 1];
                    while(!lastStep.endTime){
                        if(execution.steps.indexOf(lastStep) > 0){
                            lastStep = execution.steps[execution.steps.indexOf(lastStep) - 1];
                        } else{
                            break;
                        }
                    }
                    const exeEnd = execution.endTime ? Date.parse(execution.endTime!) : (Number.isFinite(executionActivityEndMs) ? executionActivityEndMs : (lastStep.endTime ? Date.parse(lastStep.endTime) : Date.parse(lastStep.startTime)));

                    exeDuration = (exeEnd && exeStart) ? (exeEnd - exeStart)/1000 : 0;

                    execution.steps.map((s, i) => {
                        if(s.startTime){
                            const stepSelectionKey = getExecutionStepSelectionKey(s, i);
                            const dedupeKey = JSON.stringify({
                                name: s.name,
                                description: s.description,
                                parentStepId: s.parentStepId,
                                startTime: s.startTime,
                                endTime: s.endTime,
                                duration: s.duration,
                                details: s.details,
                            });

                            if (seenStepKeys.has(dedupeKey)) {
                                return;
                            }
                            seenStepKeys.add(dedupeKey);

                            const stepStart = Date.parse(s.startTime!);
                            const rawStepEnd = (execution.endTime && !s.endTime)
                                ? Date.parse(execution.endTime!)
                                : (s.endTime ? Date.parse(s.endTime!) : executionActivityEndMs);
                            const stepEnd = Math.max(rawStepEnd, stepStart + 500);
                            const startTime = ((stepStart-exeStart)/1000);
                            const endTime = ((stepEnd-exeStart)/1000);

                            const stepEvents = getStepEvents(execution, s);
                            const errorCount = getStepErrorCount(execution, s);
                            const nonErrorEventCount = stepEvents.filter(event => !isErrorEventType(event.type)).length;
                            const designedStepNumber = getStepDesignedNumber(s);
                            const intervalDuration = Math.max(0, endTime - startTime);
                            const hasChildren = execution.steps.some(candidate => candidate.parentStepId === s.id);

                            const step = {
                                name: s.name,
                                interval: [startTime, endTime],
                                description: s.description,
                                stepId: stepSelectionKey,
                                hasErrors: errorCount > 0,
                                errorCount,
                                nonErrorEventCount,
                                completionStatus: getStepCompletionStatus(s),
                                isMandatory: isMandatoryStep(s),
                                designedStepNumber,
                                isShort: isShortStep(intervalDuration, 24 * 3),
                                isStartedOnly: !s.endTime,
                                parentStepId: s.parentStepId,
                                hasChildren,
                            };

                            formattedSteps.push(step);
                            const isChild =  s.parentStepId != null;

                            const parentIdx = isChild ? execution.steps.findIndex(st => st.id == s.parentStepId) : i;

                            const childIdx = isChild ? i - parentIdx : undefined;

                            const zone: AxisZone = {
                                index: i + 1,
                                start: startTime,
                                end: endTime,
                                label: s.name,
                                idxLabel: designedStepNumber ?? String(i + 1),
                                parentIdx: isChild ? parentIdx : undefined,
                                childIdx: childIdx,
                                id: stepSelectionKey,
                            }

                            formattedAxisSteps.push(zone);
                        }
                    })

                    if(execution.events && execution.events.length > 0){

                        const evTypes = Array.from(new Set(execution.events.map(e => e.type ?? '')));

                        eventTypes.push(...evTypes);

                        const orderedEvents = execution.events.slice().sort((e1, e2) => Date.parse(e1.timestamp!) - Date.parse(e2.timestamp!));

                        orderedEvents.map(event => {

                            const eventTime = Date.parse(event.timestamp!);
                            const eventTimeStamp = ((eventTime-exeStart)/1000);

                            const eventValue = isStateTreeNode(event.value) ? getSnapshot(event.value) : event.value;
                            const eventColorRaw = eventValue && typeof eventValue === 'object' && !Array.isArray(eventValue)
                                ? (eventValue as Record<string, unknown>)['_Color']
                                : undefined;

                            const eventData: Record<string, any> = {
                                type: (event.type),
                                time: eventTimeStamp,
                                description: event.description,
                                label: event.name,
                                parentEventId: event.parentEventId,
                                value: eventValue,
                                ...(typeof eventColorRaw === 'string' && eventColorRaw ? { color: eventColorRaw } : {}),
                            };

                            formattedEvents.push(eventData);
                        })
                    }
                }
            }
            const interval = 10;
            const TICKS = (Array.from({ length: exeDuration/interval + 1 }, (_, i) => i * interval));

            return { formattedSteps, formattedEvents, formattedAxisSteps, eventTypes, exeDuration, TICKS };
        },
        get additionalDataItems() {
            return partitionAdditionalData(self._execution.additionalData, {
                sortKeys: true,
                emptyValue: '—',
            }).infoItems;
        },
        get summaryDataItems() {
            return partitionAdditionalData(self._execution.additionalData, {
                sortKeys: true,
                emptyValue: '—',
            }).summaryItems;
        },
        get executionUIData(){
            return self._execution.getExecutionData;
        },
    }))
    .actions(self => {

        const loadExecution = flow(function* (id: string) {
            setSelectedExecutionId(id);

            self.status.setLoading();
            yield self._execution.loadExecution({id: id});
            self.status.setDone();
        });

        const setExecutionData = (data: IExecutionsStore | Record<string, any> | null) => {
            if (self.executionData) {
                destroy(self.executionData);
                self.executionData = null;
            }

            if (!data) {return;}

            const snapshot = isStateTreeNode(data) ? getSnapshot(data) : data;
            self.executionData = cast(snapshot);
        };

        const disposeExecution = () => {
            if (self.executionData) {
                destroy(self.executionData);
                self.executionData = null;
            }
            self.selectedExecutionId = null;
        };

        const setExecutionsUserId = (id: string | null | undefined) => {
            if (id) {
                self.executionsUserId = id;
            }
        }

        const setExecutionsUserContext = (context: {
            id?: string | null;
            name?: string | null;
            role?: string | null;
        }) => {
            self.executionsUserId = context.id ?? null;
            self.executionsUserName = context.name ?? null;
            self.executionsUserRole = context.role ?? null;
        };

        const clearExecutionsUserContext = () => {
            self.executionsUserId = null;
            self.executionsUserName = null;
            self.executionsUserRole = null;
        };

        const setSelectedExecutionId = (id: string | null | undefined) => {
            if (id) {
                self.selectedExecutionId = id;
            }
        };

        const clearSelectedExecutionId = () => self.selectedExecutionId = null;

        const setSelectedStepId = (id: string | null | undefined) => {
            if (id) {
                self.selectedStepId = id;
            }
        }

        const clearSelectedStepId = () => self.selectedStepId = null;

        const toggleStepModalOpen = (open: boolean) => {
            self.stepModalOpen = open;
        };

        const clearData = () =>{
            clearExecutionsUserContext();
            disposeExecution();
            clearSelectedExecutionId();
            clearSelectedStepId();
        }

        return {
            loadExecution,
            setExecutionsUserId,
            setExecutionsUserContext,
            setSelectedExecutionId,
            clearSelectedExecutionId,
            setSelectedStepId,
            clearSelectedStepId,
            toggleStepModalOpen,
            setExecutionData,
            clearData,
        }
    });

type ExecutionsUIStoreFactoryType = typeof ExecutionsUIStoreInferred;
interface IExecutionsUIStoreFactory extends ExecutionsUIStoreFactoryType { }
export const ExecutionsUIStore: IExecutionsUIStoreFactory = ExecutionsUIStoreInferred;
export interface IExecutionsUIStore extends Instance<IExecutionsUIStoreFactory> { }
export interface IExecutionsUIStoreSnapshotIn extends SnapshotIn<IExecutionsUIStore> { }
